import React, { useState, useEffect } from 'react';
import PlayerSelection from './PlayerSelection';
import CumulativeWinnings from './CumulativeWinnings';
import { readPlayers } from '../../utils/parseData';
import '../../styles/layout/_visualizationsLayout.css';

// Define the 5 fixed colors as requested
const PLAYER_COLORS = [
  '#F62D2D', // Red
  '#3b82f6', // Blue
  '#f59e0b', // Yellow
  '#22c55e', // Green
  '#C0C0C0'  // Silver
];

export default function Visualizations() {
  const [allPlayers, setAllPlayers] = useState([]);
  // select "Kyle" by default
  const [selectedPlayers, setSelectedPlayers] = useState(() => ['Kyle']); // This array preserves selection order
  
  // track persistent slot assignment per player; give Kyle the first slot by default
  const [assignedSlots, setAssignedSlots] = useState(() => ({ Kyle: 0 })); // { playerName: slotIndex }

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const playersDict = await readPlayers();
        const playerNames = Object.keys(playersDict).sort();
        setAllPlayers(playerNames);
      } catch (err) {
        console.error("Failed to fetch players", err);
      }
    };
    fetchPlayers();
  }, []);

  // Function to add/remove a player, now enforces 5-player limit
  const handleTogglePlayer = (playerName) => {
    // if currently selected -> unselect AND free their assigned slot
    if (selectedPlayers.includes(playerName)) {
      setSelectedPlayers(prev => prev.filter((p) => p !== playerName));
      setAssignedSlots(prev => {
        const next = { ...prev };
        delete next[playerName]; // free slot for reuse
        return next;
      });
      return;
    }

    // selecting a player
    // if player already has an assigned slot (should be rare now), just add them back
    if (assignedSlots[playerName] !== undefined) {
      setSelectedPlayers(prev => [...prev, playerName]);
      return;
    }

    // if no active slots available (based on currently selected players), do nothing
    if (selectedPlayers.length >= PLAYER_COLORS.length) {
      return;
    }

    // find the first unused slot index (0..PLAYER_COLORS.length-1)
    const usedSlots = new Set(Object.values(assignedSlots));
    let availableSlot = null;
    for (let i = 0; i < PLAYER_COLORS.length; i++) {
      if (!usedSlots.has(i)) {
        availableSlot = i;
        break;
      }
    }
    if (availableSlot === null) {
      // Shouldn't happen because we checked selectedPlayers.length above,
      // but guard anyway.
      return;
    }

    // assign available slot and select
    setAssignedSlots(prev => ({ ...prev, [playerName]: availableSlot }));
    setSelectedPlayers(prev => [...prev, playerName]);
  };

  // Create the color map based on assigned slot (persistent)
  const colorMap = {};
  selectedPlayers.forEach((name) => {
    const slot = assignedSlots[name];
    if (slot !== undefined && slot < PLAYER_COLORS.length) {
      colorMap[name] = PLAYER_COLORS[slot];
    }
  });

  // Flag to disable other checkboxes
  const isSelectionLocked = selectedPlayers.length === PLAYER_COLORS.length;
  
  return (
    <div className = "visualizations-container">
      <h1 className="page-title">Visualizations</h1>

      {/*White separator line*/}
      <hr className="separator-line" />

      <div className="graph-container">
        <h2 className="graph-title">Cumulative Winnings</h2>

        <PlayerSelection
        players={allPlayers}
        selectedPlayers={selectedPlayers}
        onTogglePlayer={handleTogglePlayer}
        colors={colorMap}
        isSelectionLocked={isSelectionLocked}
        defaultCollapsed={true} // collapse by default
      />
      <CumulativeWinnings
        selectedPlayers={selectedPlayers}
        colors={colorMap} // Pass the dynamic color map
      />

      </div>
    </div>
  );
}